import { useMemo } from 'react'
import useCharterRewardAvailability, {
  type CharterRewardLeg,
  type CharterRewardLegResult,
} from 'hooks/Flights/useCharterRewardAvailability'

export interface UseCharterRewardSeatsIndicatorForDateArgs {
  /** Flat list of `(journey_key, legTotal)` per leg of every visible date. The hook dedupes internally. */
  legs: Array<CharterRewardLeg>
  /** Per-date journey-key lists used to pick legs out of the flat result. Multi-city dates list all N keys. */
  legsByDate: Record<string, Array<string>>
  /** Adults + children (charter infants fly free and must be excluded). */
  passengerCount: number
  disabled?: boolean
}

export interface UseCharterRewardSeatsIndicatorForDateResult {
  isLoading: boolean
  /** True only when every leg listed in `legsByDate[dateKey]` has `available === true`. */
  isAvailableForDate: (dateKey: string) => boolean
  /** Per-leg lookup keyed by `journey_key`, exposing points + cash co-pay for cells. */
  getLegResult: (journeyKey: string) => CharterRewardLegResult | undefined
}

/**
 * Per-date Classic Rewards calendar indicator for Maldives charter.
 *
 * Treats reward fares as **all-or-nothing** across legs (return-trip = 2 legs, multi-city = N legs):
 * the indicator is on only when every leg listed for the date resolves with `available === true`
 * from svc-lux-loyalty `/rewards/availability`. The chunked/dedup-aware fetch is delegated to
 * {@link useCharterRewardAvailability}; this hook layers per-date aggregation on top.
 */
export default function useCharterRewardSeatsIndicatorForDate(
  args: UseCharterRewardSeatsIndicatorForDateArgs,
): UseCharterRewardSeatsIndicatorForDateResult {
  const { legs, legsByDate, passengerCount, disabled } = args

  const availability = useCharterRewardAvailability({
    legs,
    passengerCount,
    disabled,
  })

  const { availableJourneyKeys, legResultByKey } = useMemo(() => {
    const available = new Set<string>()
    const byKey = new Map<string, CharterRewardLegResult>()
    for (const leg of availability.legs) {
      byKey.set(leg.journeyKey, leg)
      if (leg.available) available.add(leg.journeyKey)
    }
    return { availableJourneyKeys: available, legResultByKey: byKey }
  }, [availability.legs])

  const isLoading = availability.isLoading

  return useMemo<UseCharterRewardSeatsIndicatorForDateResult>(() => ({
    isLoading,
    isAvailableForDate: (dateKey) => {
      if (isLoading) return false
      const journeyKeys = legsByDate[dateKey]
      if (!journeyKeys || journeyKeys.length === 0) return false
      return journeyKeys.every(key => availableJourneyKeys.has(key))
    },
    getLegResult: (journeyKey) => legResultByKey.get(journeyKey),
  }), [isLoading, legsByDate, availableJourneyKeys, legResultByKey])
}
